import { observer } from "mobx-react-lite";
import { FC, useEffect, useRef } from "react";

import {
  CheckboxVisibility,
  DetailsListLayoutMode,
  SelectionMode,
  Stack,
  TextBadge,
  TextBadgeColor,
  TextBadgeSize
} from "@bps/fluent-ui";
import { XeroTenantDto } from "@libs/gateways/custom-integration/CustomIntegrationXeroGateway.dtos.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { Selection } from "@ui-components/ShimmeredDetailsList/Selection.ts";
import { ShimmeredDetailsList } from "@ui-components/ShimmeredDetailsList/ShimmeredDetailsList.tsx";

import { useIntegrationScreenContext } from "../../context/IntegrationScreenContext.ts";
import { XeroOrganisationMenu } from "./XeroOrganisationMenu.tsx";

export const XeroOrganisationSelector: FC = observer(() => {
  const { notification, customIntegrationXero } = useStores();

  const context = useIntegrationScreenContext();
  const { setOrganisationError, xeroTenants } = context;

  const handleUpdate = async (values: XeroTenantDto) => {
    if (!values?.tenantId) {
      notification.error("Please select an organisation.");
      return;
    }
    await customIntegrationXero.setXeroTenant(values.tenantId);
    notification.success("Selected organisation has been updated.");
  };

  const selection = useRef(
    new Selection({
      onSelectionChanged: () => {
        const indexes = selection.current.getSelectedIndices();
        const itemKey = indexes[0];
        const item = selection.current.getItems()[itemKey] as XeroTenantDto;

        if (item && item.tenantId !== context.xeroTenantId) {
          handleUpdate(item);
        }
      }
    })
  );

  useEffect(() => {
    if (context.xeroTenantId !== undefined && xeroTenants.length > 0) {
      const defaultItem = xeroTenants.findIndex(
        tenant => tenant.tenantId === context.xeroTenantId
      );
      if (defaultItem !== -1) {
        selection.current.setAllSelected(false);
        selection.current.setIndexSelected(defaultItem, true, false);
      }
    }
  }, [context, xeroTenants]);

  return (
    <Stack tokens={{ childrenGap: 12, padding: 12 }}>
      <ShimmeredDetailsList
        items={xeroTenants}
        selectionPreservedOnEmptyClick={true}
        selectionMode={SelectionMode.single}
        checkboxVisibility={
          xeroTenants.length === 1
            ? CheckboxVisibility.hidden
            : CheckboxVisibility.always
        }
        selection={selection.current}
        columns={[
          {
            key: "kebabBtn",
            name: "",
            fieldName: "kebabBtn",
            minWidth: 30,
            onRender: (childTenantInfo: XeroTenantDto) => (
              <XeroOrganisationMenu
                tenantId={childTenantInfo.tenantId}
                setError={setOrganisationError}
              />
            )
          },
          {
            key: "tenantName",
            name: "Organisation",
            fieldName: "tenantName",
            minWidth: 400,
            isResizable: true
          },
          {
            key: "status",
            name: "Status",
            fieldName: "status",
            minWidth: 100,
            isResizable: true,
            onRender: () => (
              <TextBadge
                badgeSize={TextBadgeSize.small}
                badgeColor={TextBadgeColor.green}
              >
                Connected
              </TextBadge>
            )
          }
        ]}
        layoutMode={DetailsListLayoutMode.fixedColumns}
      />
    </Stack>
  );
});
